import React, { ReactNode } from 'react';
import { StyleSheet, Text, View, Pressable } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { LockIcon } from '../../resources/svgComponents/LockIcon';
import { RouteProp, useRoute } from '@react-navigation/native';
import { RootStackParamList } from '../../navigators/RootNavigator';
import HexagonProgress from './HexagonProgress';
import { useSignedInContext } from '../../context/SignedInContext';
import {
  GAME_LINK_TYPE,
  GAME_TYPES_CONFIG,
  GAME_TYPES_TYPE,
  operandIcon,
} from '../../constants/constants';
import getJourneyStages from '../../utils/journeysUtils';

interface IBigRadioProps {
  title?: string;
  onPress: () => void;
  gameType: GAME_TYPES_TYPE;
  disabled?: boolean;
  additionalIcon?: ReactNode;
  textBackground?: string;
  isLocked?: boolean;
  progress?: number;
  hiveQuery?: boolean;
}

const BigRadio = (props: IBigRadioProps) => {
  const {
    disabled,
    isLocked,
    title,
    onPress,
    additionalIcon,
    textBackground,
    gameType,
    progress = 0,
    hiveQuery = false,
  } = props;

  const route = useRoute<RouteProp<RootStackParamList, 'SelectGameType'>>();
  const { journey } = route.params || false;
  // TODO: fetch from backend

  const OperationIcon = operandIcon[GAME_TYPES_CONFIG[gameType].operation];

  return (
    <View>
      {(hiveQuery || (!disabled && journey)) && (
        <View style={[styles.hexagonProgressContainer]}>
          <HexagonProgress percentage={progress} disabled={disabled} />
        </View>
      )}

      <LinearGradient
        colors={
          disabled
            ? ['rgb(212, 212, 212)', 'rgb(188, 188, 188)']
            : ['rgba(255, 207, 83, 0.55)', 'rgba(255, 153, 0, 0.55)']
        }
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.gradientBorder}
      >
        <View style={[styles.innerContainer, disabled && { backgroundColor: '#ECECEC' }]}>
          <Pressable disabled={disabled} onPress={onPress} style={styles.btnContainer}>
            {disabled ? (
              <View style={styles.disabledTextContainer}>
                <Text maxFontSizeMultiplier={1} style={styles.disabledText}>
                  {title ?? GAME_TYPES_CONFIG[gameType].name}
                </Text>
                <View pointerEvents="none" style={styles.additionalIcon}>
                  {additionalIcon ?? <OperationIcon />}
                </View>
                <View style={styles.lockIcon}>
                  <LockIcon />
                </View>
              </View>
            ) : (
              <View style={[styles.activeTextContainer, journey && { width: 330 }]}>
                {/* Progress bar behind everything */}
                {route.params?.journey && (
                  <View style={styles.progressBarBackground}>
                    <View
                      style={[
                        styles.progressBarFill,
                        { width: `${Math.max(progress - (10 * progress) / 100, 0)}%` },
                      ]}
                    />
                  </View>
                )}

                {/* Foreground content */}
                <Text
                  maxFontSizeMultiplier={1}
                  style={[styles.activeText, { backgroundColor: textBackground || 'transparent' }]}
                >
                  {title ?? GAME_TYPES_CONFIG[gameType].name}
                </Text>
                <View pointerEvents="none" style={styles.additionalIcon}>
                  {additionalIcon ?? <OperationIcon />}
                </View>
                {isLocked && (
                  <View style={styles.lockIcon}>
                    <LockIcon />
                  </View>
                )}
              </View>
            )}
          </Pressable>
        </View>
      </LinearGradient>
    </View>
  );
};

export { BigRadio };

const styles = StyleSheet.create({
  gradientBorder: {
    borderRadius: 16,
    padding: 2.5,
    height: 68,
  },
  innerContainer: {
    borderRadius: 14,
    backgroundColor: '#FFF8E3',
    overflow: 'hidden',
    width: 330,
  },
  btnContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },

  activeTextContainer: {
    position: 'relative',
    height: 60,
    width: 343.96,
    justifyContent: 'center',
    borderRadius: 16,
    backgroundColor: '#FFF5DD',
    opacity: 0.8,
    overflow: 'hidden',
  },
  activeText: {
    fontFamily: 'Secular One',
    fontSize: 16,
    fontWeight: '400',
    color: '#121212',
    paddingVertical: 3,
    paddingHorizontal: 12,
    marginLeft: 55, // Space for additional icon
  },

  disabledTextContainer: {
    height: 58.72,
    width: 343.96,
    justifyContent: 'center',
    borderRadius: 16,
    backgroundColor: '#F3F3F3',
  },
  disabledText: {
    fontFamily: 'Secular One',
    fontSize: 16,
    fontWeight: '400',
    marginLeft: 50,
    color: '#737373',
    paddingVertical: 3,
    paddingHorizontal: 12,
  },
  additionalIcon: {
    position: 'absolute',
    left: 10,
  },
  lockIcon: {
    position: 'absolute',
    right: 17,
  },

  progressBarBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: '100%',
    borderRadius: 14,
    overflow: 'hidden',
    zIndex: -1, // Keeps it behind text/icons
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#FEDD88',
  },
  hexagonProgressContainer: {
    position: 'absolute',
    top: 0,
    right: -17,
    zIndex: 20,
  },
});
